import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { mensagemDeErro } from '@/lib/format';
import {
  adicionarAnexo,
  adicionarParte,
  chavesDeContratos,
  criarContrato,
  criarTermo,
  type ContractAttachmentKind,
  type ContractType,
  type EntitlementKind,
  type NovoAnexo,
  type NovaParte,
  type PaymentFrequency,
} from '@/features/contratos/contratos.api';
import { chavesDeClientes, criarCliente, listarClientes, obterCliente } from '@/features/clientes/clientes.api';
import { listarServicos, criarServico, chavesCatalogo } from '@/features/catalog/catalog.api';
import { listarPoliticasSla, criarPoliticaSla, listarOperadores, chavesDeAdministracao } from '@/features/admin/admin.api';
import { CLASSE_DE_CAMPO } from '@/features/admin/componentes';
import { TIPOS_DE_CONTRATO, type Tom } from '@/features/contratos/navegacao';
import type { ContractDirection } from '@/features/contratos/contratos.api';

/**
 * As peças que as duas telas de contrato compartilham.
 *
 * A tabela, o campo de formulário e a janela modal **não** estão aqui: já
 * existem em `features/admin/componentes` e em `features/tickets/Modal`, e são
 * reusados pelo alias. Mesma decisão de `features/clientes/componentes` —
 * duas tabelas divergem no primeiro ajuste de espaçamento.
 */

const TONS: Record<Tom, string> = {
  neutro: 'bg-muted text-muted-foreground ring-border',
  sucesso: 'bg-success/12 text-success ring-success/25',
  alerta: 'bg-warning/12 text-warning ring-warning/25',
  perigo: 'bg-destructive/12 text-destructive ring-destructive/25',
  info: 'bg-primary/10 text-primary ring-primary/25',
};

/**
 * Situação em cor **e** em palavra.
 *
 * Cor sozinha não comunica para daltônicos, e aqui são cinco estados: a
 * distância entre "vencido" e "rescindido" precisa estar escrita.
 */
export function Etiqueta({
  rotulo,
  tom,
  titulo,
}: {
  rotulo: string;
  tom: Tom;
  titulo?: string;
}) {
  return (
    <span
      title={titulo}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        TONS[tom],
      )}
    >
      {rotulo}
    </span>
  );
}

/** Um número grande com legenda — a unidade do painel do contrato. */
export function Indicador({
  rotulo,
  valor,
  nota,
  tom = 'neutro',
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  tom?: Tom;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{rotulo}</p>
      <p
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums',
          tom === 'perigo' && 'text-destructive',
          tom === 'alerta' && 'text-warning',
          tom === 'sucesso' && 'text-success',
        )}
      >
        {valor}
      </p>
      {nota ? <p className="mt-1 text-xs text-muted-foreground">{nota}</p> : null}
    </div>
  );
}

/**
 * Um aviso que explica em vez de só alertar.
 *
 * É a peça do critério 12.21: chamado cuja conta difere da do contrato vira
 * **aviso na tela**, nunca erro de gravação. O texto tem de dizer por que
 * aquilo é normal, senão vira um alarme que as pessoas aprendem a ignorar.
 */
export function Aviso({
  tom = 'alerta',
  titulo,
  children,
}: {
  tom?: Tom;
  titulo: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-lg px-4 py-3 text-sm ring-1 ring-inset',
        tom === 'alerta' && 'bg-warning/8 text-foreground ring-warning/25',
        tom === 'perigo' && 'bg-destructive/8 text-foreground ring-destructive/25',
        tom === 'info' && 'bg-primary/8 text-foreground ring-primary/25',
        (tom === 'neutro' || tom === 'sucesso') && 'bg-muted text-foreground ring-border',
      )}
      role="status"
    >
      <p className="font-medium">{titulo}</p>
      {children ? <div className="mt-1 text-muted-foreground">{children}</div> : null}
    </div>
  );
}

/**
 * Envolve uma escrita de contrato: invalida a raiz da feature e guarda o erro.
 *
 * Invalidar a raiz inteira é deliberado — ativar um termo muda a situação na
 * lista, muda o painel e tira uma linha da fila de sobreposições. Uma
 * invalidação cirúrgica aqui seria uma lista de chaves para esquecer.
 */
export function useEscritaDeContrato<TEntrada = void, TSaida = unknown>(
  escrever: (entrada: TEntrada) => Promise<TSaida>,
  aoConcluir?: (saida: TSaida) => void,
) {
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const mutacao = useMutation<TSaida, Error, TEntrada>({
    mutationFn: escrever,
    onMutate: () => setErro(null),
    onSuccess: (saida) => {
      void cliente.invalidateQueries({ queryKey: chavesDeContratos.raiz });
      aoConcluir?.(saida);
    },
    onError: (falha) => setErro(mensagemDeErro(falha) ?? 'Não foi possível salvar.'),
  });

  return { mutacao, erro, limparErro: () => setErro(null) };
}

function SeletorDeCliente({
  valor,
  aoSelecionar,
  desabilitado,
}: {
  valor: { id: string; nome: string } | null;
  aoSelecionar: (conta: { id: string; nome: string } | null) => void;
  desabilitado?: boolean;
}) {
  const [busca, setBusca] = useState(valor?.nome ?? '');
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: [...chavesDeClientes.lista({}), 'busca', busca],
    queryFn: () => listarClientes({ q: busca }),
    enabled: busca.length >= 1,
    staleTime: 10_000,
  });

  useEffect(() => {
    function fecharFora(e: MouseEvent) {
      if (raiz.current && !raiz.current.contains(e.target as Node)) {
        setAberto(false);
      }
    }
    document.addEventListener('mousedown', fecharFora);
    return () => document.removeEventListener('mousedown', fecharFora);
  }, []);

  if (desabilitado && valor) {
    return (
      <input className={CLASSE_DE_CAMPO} value={valor.nome} readOnly disabled />
    );
  }

  return (
    <div ref={raiz} className="relative">
      <input
        className={CLASSE_DE_CAMPO}
        placeholder="Digite o nome do cliente..."
        value={busca}
        autoComplete="off"
        onChange={(e) => {
          setBusca(e.target.value);
          setAberto(true);
          if (!e.target.value) aoSelecionar(null);
        }}
        onFocus={() => busca.length >= 1 && setAberto(true)}
      />
      {aberto && data && data.items.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-background shadow-lg">
          {data.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  aoSelecionar({ id: c.id, nome: c.name });
                  setBusca(c.name);
                  setAberto(false);
                }}
              >
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {aberto && data && data.items.length === 0 && busca.length >= 1 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground shadow-lg">
          Nenhum cliente encontrado.
        </div>
      )}
    </div>
  );
}

const WIZARD_ATTACHMENT_KINDS: { valor: ContractAttachmentKind; rotulo: string }[] = [
  { valor: 'CONTRATO', rotulo: 'Contrato' },
  { valor: 'EDITAL', rotulo: 'Edital' },
  { valor: 'PROPOSTA', rotulo: 'Proposta' },
  { valor: 'GARANTIA', rotulo: 'Garantia' },
  { valor: 'ADITIVO', rotulo: 'Aditivo' },
  { valor: 'OUTRO', rotulo: 'Outro' },
];

type LocalParte = NovaParte & { _lid: number };
type LocalAnexo = NovoAnexo & { _lid: number };

let _wizLid = 0;
const nextLid = () => ++_wizLid;

function DemandanteForm({
  jaTemTitular,
  aoAdicionar,
}: {
  jaTemTitular: boolean;
  aoAdicionar: (d: NovaParte) => void;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [titular, setTitular] = useState(false);

  function adicionar() {
    if (!nome.trim()) return;
    aoAdicionar({
      side: 'CLIENTE',
      role: 'DEMANDANTE',
      isPrimary: titular && !jaTemTitular,
      name: nome.trim(),
      email: email.trim() || undefined,
    });
    setNome(''); setEmail(''); setTitular(false);
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          className={CLASSE_DE_CAMPO}
          placeholder="Nome *"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <input
          className={CLASSE_DE_CAMPO}
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="wiz-dem-titular"
            checked={titular}
            onChange={(e) => setTitular(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
            disabled={jaTemTitular}
          />
          <label htmlFor="wiz-dem-titular" className="cursor-pointer text-sm">Titular</label>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={adicionar} disabled={!nome.trim()}>
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          Adicionar demandante
        </Button>
      </div>
    </div>
  );
}

/**
 * Wizard de cadastro de contrato — 4 etapas.
 *
 * Exportado para que a ficha do cliente possa abri-lo pré-preenchido com o
 * `accountId` do cliente. Quando `accountIdInicial` é informado o campo de
 * cliente fica desabilitado: o operador já está dentro do cliente e mudar
 * seria um bug, não uma escolha.
 *
 * Toda a criação (contrato → termo → partes → anexos) acontece em sequência
 * ao clicar "Criar contrato" na etapa 4. Os passos anteriores apenas acumulam
 * estado local — não há salvamento parcial.
 */
export function DialogoDeContrato({
  aberto,
  aoFechar,
  accountIdInicial,
}: {
  aberto: boolean;
  aoFechar: () => void;
  accountIdInicial?: string;
}) {
  const qc = useQueryClient();
  const [etapa, setEtapa] = useState(1);

  // ── Etapa 1: Identificação ─────────────────────────────────────────────────
  const [conta, setConta] = useState<{ id: string; nome: string } | null>(null);
  const { data: clientePreSelecionado } = useQuery({
    queryKey: chavesDeClientes.ficha(accountIdInicial ?? ''),
    queryFn: () => obterCliente(accountIdInicial!),
    enabled: !!accountIdInicial,
    staleTime: Infinity,
  });
  const [direcao, setDirecao] = useState<ContractDirection>('OUTBOUND');
  const [nome, setNome] = useState('');
  const [numeroExterno, setNumeroExterno] = useState('');
  const [tipo, setTipo] = useState<ContractType>('MANAGED_SERVICE');
  const [assinadoPor, setAssinadoPor] = useState('');
  const [assinadoEm, setAssinadoEm] = useState('');
  const [areaDemandante, setAreaDemandante] = useState('');

  // Criação rápida de cliente inline
  const [criandoCliente, setCriandoCliente] = useState(false);
  const [novoClienteNome, setNovoClienteNome] = useState('');
  const [novoClienteCodigo, setNovoClienteCodigo] = useState('');
  const [novoClienteCnpj, setNovoClienteCnpj] = useState('');
  const [erroClienteInline, setErroClienteInline] = useState<string | null>(null);
  const [salvandoCliente, setSalvandoCliente] = useState(false);

  // ── Etapa 2: Primeiro Termo ────────────────────────────────────────────────
  const [inicioTermo, setInicioTermo] = useState('');
  const [fimTermo, setFimTermo] = useState('');
  const [valorMensal, setValorMensal] = useState('');
  const [valorTotal, setValorTotal] = useState('');
  const [frequenciaPagamento, setFrequenciaPagamento] = useState<PaymentFrequency | ''>('');
  const [valorEmpenho, setValorEmpenho] = useState('');
  const [saldoLiquidar, setSaldoLiquidar] = useState('');
  const [empenho1Grau, setEmpenho1Grau] = useState('');
  const [empenho2Grau, setEmpenho2Grau] = useState('');
  const [franquiaTipo, setFranquiaTipo] = useState<EntitlementKind | ''>('');
  const [franquiaQtd, setFranquiaQtd] = useState('');
  const [autoRenovacao, setAutoRenovacao] = useState(false);
  const [mesesRenovacao, setMesesRenovacao] = useState('12');

  // ── Etapa 3: Serviços ──────────────────────────────────────────────────────
  const [servicosContrato, setServicosContrato] = useState<{ serviceId: string; slaPolicyId: string }[]>([]);
  const { data: catalogoServicos } = useQuery({
    queryKey: chavesCatalogo.lista(undefined, 'CATALOGUE'),
    queryFn: () => listarServicos(undefined, 'CATALOGUE'),
    staleTime: 60_000,
  });
  const { data: politicasSla } = useQuery({
    queryKey: chavesDeAdministracao.politicasSla,
    queryFn: listarPoliticasSla,
    staleTime: 60_000,
  });

  // mini-form: novo serviço
  const [novoServico, setNovoServico] = useState(false);
  const [nomeServico, setNomeServico] = useState('');
  const [tipoServico, setTipoServico] = useState<'BUSINESS' | 'TECHNICAL'>('BUSINESS');
  const mutacaoServico = useMutation({
    mutationFn: () =>
      criarServico({
        key: nomeServico.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40),
        name: nomeServico.trim(),
        serviceType: tipoServico,
        portfolioStage: 'CATALOGUE',
      }),
    onSuccess: (svc) => {
      qc.invalidateQueries({ queryKey: chavesCatalogo.lista(undefined, 'CATALOGUE') });
      setServicosContrato((prev) => [...prev, { serviceId: svc.id, slaPolicyId: '' }]);
      setNomeServico('');
      setNovoServico(false);
    },
  });

  // mini-form: nova política de ANS
  const [novaSla, setNovaSla] = useState(false);
  const [nomeSla, setNomeSla] = useState('');
  const [kindSla, setKindSla] = useState<'SLA' | 'OLA' | 'UC'>('SLA');
  const [metricaSla, setMetricaSla] = useState<'FIRST_RESPONSE' | 'RESOLUTION' | 'UPDATE_INTERVAL'>('RESOLUTION');
  const [alvoSla, setAlvoSla] = useState('');
  const mutacaoSla = useMutation({
    mutationFn: () =>
      criarPoliticaSla({
        key: nomeSla.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 40),
        name: nomeSla.trim(),
        kind: kindSla,
        metric: metricaSla,
        targetMinutes: Number(alvoSla),
        contractual: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chavesDeAdministracao.politicasSla });
      setNomeSla('');
      setAlvoSla('');
      setNovaSla(false);
    },
  });

  // ── Etapa 4: Partes e Documentos ───────────────────────────────────────────
  const [partes, setPartes] = useState<LocalParte[]>([]);
  const [anexos, setAnexos] = useState<LocalAnexo[]>([]);
  const { data: operadores } = useQuery({
    queryKey: chavesDeAdministracao.operadores,
    queryFn: listarOperadores,
    staleTime: 60_000,
  });

  // Formulário de preposto
  const [prepOpId, setPrepOpId] = useState('');
  const [prepNome, setPrepNome] = useState('');
  const [prepEmail, setPrepEmail] = useState('');
  const [prepTel, setPrepTel] = useState('');

  // Formulário de fiscal
  const [fiscNome, setFiscNome] = useState('');
  const [fiscEmail, setFiscEmail] = useState('');
  const [fiscTel, setFiscTel] = useState('');
  const [fiscTitular, setFiscTitular] = useState(false);

  // Formulário de anexo
  const [anexTipo, setAnexTipo] = useState<ContractAttachmentKind>('CONTRATO');
  const [anexRotulo, setAnexRotulo] = useState('');
  const [anexUrl, setAnexUrl] = useState('');

  // ── Envio ──────────────────────────────────────────────────────────────────
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Auto-preenche campos do preposto ao selecionar operador
  useEffect(() => {
    if (prepOpId && operadores) {
      const op = operadores.items.find((u) => u.id === prepOpId);
      if (op) {
        setPrepNome(op.name);
        setPrepEmail(op.email);
        setPrepTel(op.phone ?? '');
      }
    }
  }, [prepOpId, operadores]);

  useEffect(() => {
    if (!aberto) {
      setEtapa(1);
      setConta(null);
      setDirecao('OUTBOUND');
      setNome(''); setNumeroExterno(''); setTipo('MANAGED_SERVICE');
      setAssinadoPor(''); setAssinadoEm(''); setAreaDemandante('');
      setInicioTermo(''); setFimTermo('');
      setValorMensal(''); setValorTotal('');
      setFrequenciaPagamento(''); setValorEmpenho(''); setSaldoLiquidar('');
      setEmpenho1Grau(''); setEmpenho2Grau('');
      setFranquiaTipo(''); setFranquiaQtd('');
      setAutoRenovacao(false); setMesesRenovacao('12');
      setServicosContrato([]);
      setNovoServico(false); setNomeServico(''); setTipoServico('BUSINESS');
      setNovaSla(false); setNomeSla(''); setKindSla('SLA'); setMetricaSla('RESOLUTION'); setAlvoSla('');
      setPartes([]); setAnexos([]);
      setPrepOpId(''); setPrepNome(''); setPrepEmail(''); setPrepTel('');
      setFiscNome(''); setFiscEmail(''); setFiscTel(''); setFiscTitular(false);
      setAnexTipo('CONTRATO'); setAnexRotulo(''); setAnexUrl('');
      setCriandoCliente(false); setNovoClienteNome(''); setNovoClienteCodigo(''); setNovoClienteCnpj('');
      setErroClienteInline(null);
      setErro(null);
    }
  }, [aberto]);

  const accountId = accountIdInicial ?? conta?.id ?? '';
  const etapa1Valida = !!accountId && nome.trim().length >= 2;
  const etapa2Valida = inicioTermo.length >= 10;

  function adicionarPreposto() {
    if (!prepNome.trim()) return;
    setPartes((prev) => [
      ...prev,
      {
        side: 'APTUM', role: 'PREPOSTO',
        userId: prepOpId || undefined,
        name: prepNome, email: prepEmail || undefined, phone: prepTel || undefined,
        _lid: nextLid(),
      },
    ]);
    setPrepOpId(''); setPrepNome(''); setPrepEmail(''); setPrepTel('');
  }

  function adicionarFiscal() {
    if (!fiscNome.trim()) return;
    const jaTitular = partes.some((p) => p.side === 'CLIENTE' && p.isPrimary);
    setPartes((prev) => [
      ...prev,
      {
        side: 'CLIENTE', role: 'FISCAL',
        isPrimary: fiscTitular && !jaTitular,
        name: fiscNome, email: fiscEmail || undefined, phone: fiscTel || undefined,
        _lid: nextLid(),
      },
    ]);
    setFiscNome(''); setFiscEmail(''); setFiscTel(''); setFiscTitular(false);
  }

  function adicionarAnexoLocal() {
    if (!anexUrl.trim()) return;
    const rotuloPadrao = WIZARD_ATTACHMENT_KINDS.find((k) => k.valor === anexTipo)?.rotulo ?? anexTipo;
    setAnexos((prev) => [
      ...prev,
      { kind: anexTipo, label: anexRotulo.trim() || rotuloPadrao, url: anexUrl.trim(), _lid: nextLid() },
    ]);
    setAnexRotulo(''); setAnexUrl(''); setAnexTipo('CONTRATO');
  }

  async function criarClienteInline() {
    if (!novoClienteNome.trim() || !novoClienteCodigo.trim()) return;
    setSalvandoCliente(true);
    setErroClienteInline(null);
    try {
      const cliente = await criarCliente({
        code: novoClienteCodigo.trim(),
        legalName: novoClienteNome.trim(),
        taxId: novoClienteCnpj.trim() || undefined,
      });
      setConta({ id: cliente.id, nome: cliente.name });
      void qc.invalidateQueries({ queryKey: chavesDeClientes.lista({}) });
      setCriandoCliente(false);
      setNovoClienteNome(''); setNovoClienteCodigo(''); setNovoClienteCnpj('');
    } catch (e) {
      setErroClienteInline(mensagemDeErro(e) ?? 'Não foi possível criar o cliente.');
    } finally {
      setSalvandoCliente(false);
    }
  }

  async function submeter() {
    if (!accountId || !nome || !inicioTermo) return;
    setEnviando(true);
    setErro(null);
    try {
      const contrato = await criarContrato({
        accountId,
        name: nome,
        externalNumber: numeroExterno || undefined,
        type: tipo,
        direction: direcao,
        signedBy: assinadoPor || undefined,
        signedOnDate: assinadoEm || undefined,
        demandingArea: areaDemandante || undefined,
      });
      const id = contrato.id;
      await criarTermo(id, {
        startsAt: inicioTermo,
        endsAt: fimTermo || undefined,
        monthlyValue: valorMensal || undefined,
        totalValue: valorTotal || undefined,
        paymentFrequency: (frequenciaPagamento as PaymentFrequency) || undefined,
        commitmentValue: valorEmpenho || undefined,
        balanceToPay: saldoLiquidar || undefined,
        commitment1stGrade: empenho1Grau || undefined,
        commitment2ndGrade: empenho2Grau || undefined,
        entitlementKind: (franquiaTipo as EntitlementKind) || undefined,
        entitlementAmount: franquiaQtd ? Number(franquiaQtd) : undefined,
        autoRenew: autoRenovacao,
        autoRenewMonths: autoRenovacao ? Number(mesesRenovacao) : undefined,
        services:
          servicosContrato.length > 0
            ? servicosContrato.map((s) => ({
                serviceId: s.serviceId,
                slaPolicyId: s.slaPolicyId || undefined,
              }))
            : undefined,
      });
      for (const { _lid: _l, ...parte } of partes) await adicionarParte(id, parte);
      for (const { _lid: _l, ...anexo } of anexos) await adicionarAnexo(id, anexo);
      void qc.invalidateQueries({ queryKey: chavesDeContratos.raiz });
      aoFechar();
    } catch (e) {
      setErro(mensagemDeErro(e) ?? 'Não foi possível criar o contrato.');
    } finally {
      setEnviando(false);
    }
  }

  if (!aberto) return null;

  const ETAPAS = ['Identificação', 'Primeiro Termo', 'Serviços', 'Partes e Documentos'];
  const prepostos = partes.filter((p) => p.side === 'APTUM');
  const fiscais = partes.filter((p) => p.side === 'CLIENTE');

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-foreground/30 backdrop-blur-[2px]"
        onClick={() => !enviando && aoFechar()}
        aria-hidden
      />
      <div className="relative w-full max-w-2xl animate-slide-up rounded-lg border border-border bg-card shadow-elevation">

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
          <h2 className="font-heading text-base font-semibold leading-tight">Novo Contrato</h2>
          <button
            type="button"
            onClick={() => !enviando && aoFechar()}
            aria-label="Fechar"
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex border-b border-border">
          {ETAPAS.map((rotulo, i) => {
            const num = i + 1;
            const ativa = etapa === num;
            const concluida = etapa > num;
            return (
              <button
                key={num}
                type="button"
                onClick={() => { if (concluida && !enviando) setEtapa(num); }}
                className={cn(
                  'flex flex-1 flex-col items-center gap-1 px-2 py-3 text-xs transition-colors',
                  ativa && 'border-b-2 border-primary text-primary',
                  !ativa && concluida && 'cursor-pointer text-muted-foreground hover:text-foreground',
                  !ativa && !concluida && 'cursor-default text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold',
                    ativa && 'bg-primary text-primary-foreground',
                    concluida && 'bg-success/15 text-success',
                    !ativa && !concluida && 'bg-muted text-muted-foreground',
                  )}
                >
                  {concluida ? '✓' : num}
                </span>
                <span className="hidden font-medium sm:block">{rotulo}</span>
              </button>
            );
          })}
        </div>

        {/* Step content */}
        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4">

          {/* ── 1: Identificação ──────────────────────────────────────────────── */}
          {etapa === 1 && (
            <>
              {/* Direção do contrato */}
              <div>
                <label className="mb-1 block text-sm font-medium">Direção</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setDirecao('OUTBOUND'); setTipo('MANAGED_SERVICE'); }}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      direcao === 'OUTBOUND'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/50',
                    )}
                  >
                    Para o cliente
                    <p className="mt-0.5 text-xs font-normal opacity-70">Prestamos serviço ao cliente</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDirecao('INBOUND'); setTipo('PJ_SERVICES'); }}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      direcao === 'INBOUND'
                        ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                        : 'border-border text-muted-foreground hover:border-amber-500/50',
                    )}
                  >
                    PJ / Fornecedor
                    <p className="mt-0.5 text-xs font-normal opacity-70">Recebemos serviço externo</p>
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  {direcao === 'INBOUND' ? 'Empresa / PJ' : 'Cliente'}{' '}
                  <span className="text-destructive" aria-hidden>*</span>
                </label>
                {accountIdInicial ? (
                  <input className={CLASSE_DE_CAMPO} value={clientePreSelecionado?.name ?? '…'} readOnly disabled />
                ) : criandoCliente ? (
                  <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
                    <p className="text-xs font-semibold text-muted-foreground">Cadastrar novo cliente</p>
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Razão social *"
                      value={novoClienteNome}
                      onChange={(e) => setNovoClienteNome(e.target.value)}
                      autoFocus
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className={CLASSE_DE_CAMPO}
                        placeholder="Código (ex: TJMT) *"
                        value={novoClienteCodigo}
                        onChange={(e) => setNovoClienteCodigo(e.target.value)}
                      />
                      <input
                        className={CLASSE_DE_CAMPO}
                        placeholder="CNPJ"
                        value={novoClienteCnpj}
                        onChange={(e) => setNovoClienteCnpj(e.target.value)}
                      />
                    </div>
                    {erroClienteInline && (
                      <p className="text-xs text-destructive">{erroClienteInline}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        loading={salvandoCliente}
                        disabled={!novoClienteNome.trim() || !novoClienteCodigo.trim()}
                        onClick={criarClienteInline}
                      >
                        Salvar cliente
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => { setCriandoCliente(false); setErroClienteInline(null); }}
                        disabled={salvandoCliente}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <SeletorDeCliente valor={conta} aoSelecionar={setConta} />
                    <button
                      type="button"
                      className="mt-1.5 text-xs text-primary hover:underline"
                      onClick={() => setCriandoCliente(true)}
                    >
                      + Criar novo cliente
                    </button>
                  </>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  Nome do contrato <span className="text-destructive" aria-hidden>*</span>
                </label>
                <input
                  className={CLASSE_DE_CAMPO}
                  placeholder="Ex: Gestão de TI — Lote 2"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Nº do cliente</label>
                  <input
                    className={CLASSE_DE_CAMPO}
                    placeholder="Ex: TJMT_016_2020"
                    value={numeroExterno}
                    onChange={(e) => setNumeroExterno(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Tipo</label>
                  <select
                    className={CLASSE_DE_CAMPO}
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value as ContractType)}
                  >
                    {TIPOS_DE_CONTRATO
                      .filter((t) =>
                        direcao === 'INBOUND'
                          ? ['PJ_SERVICES', 'SUPPLIER', 'UNDERPINNING'].includes(t.valor)
                          : !['PJ_SERVICES', 'SUPPLIER'].includes(t.valor),
                      )
                      .map((t) => (
                        <option key={t.valor} value={t.valor}>{t.rotulo}</option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Assinado por</label>
                  <input
                    className={CLASSE_DE_CAMPO}
                    placeholder="Nome do representante"
                    value={assinadoPor}
                    onChange={(e) => setAssinadoPor(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data de assinatura</label>
                  <input
                    type="date"
                    className={CLASSE_DE_CAMPO}
                    value={assinadoEm}
                    onChange={(e) => setAssinadoEm(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Área demandante</label>
                <input
                  className={CLASSE_DE_CAMPO}
                  placeholder="Ex: Secretaria de TI"
                  value={areaDemandante}
                  onChange={(e) => setAreaDemandante(e.target.value)}
                />
              </div>
            </>
          )}

          {/* ── 2: Primeiro Termo ─────────────────────────────────────────────── */}
          {etapa === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Início <span className="text-destructive" aria-hidden>*</span>
                  </label>
                  <input
                    type="date"
                    className={CLASSE_DE_CAMPO}
                    value={inicioTermo}
                    onChange={(e) => setInicioTermo(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Término</label>
                  <input
                    type="date"
                    className={CLASSE_DE_CAMPO}
                    value={fimTermo}
                    onChange={(e) => setFimTermo(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Valor mensal (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={valorMensal}
                    onChange={(e) => setValorMensal(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Valor total (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={valorTotal}
                    onChange={(e) => setValorTotal(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Frequência de pagamento</label>
                <select
                  className={CLASSE_DE_CAMPO}
                  value={frequenciaPagamento}
                  onChange={(e) => setFrequenciaPagamento(e.target.value as PaymentFrequency | '')}
                >
                  <option value="">Não especificada</option>
                  <option value="MONTHLY">Mensal</option>
                  <option value="QUARTERLY">Trimestral</option>
                  <option value="SEMIANNUAL">Semestral</option>
                  <option value="ANNUAL">Anual</option>
                  <option value="ONE_TIME">Único</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Valor do empenho (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={valorEmpenho}
                    onChange={(e) => setValorEmpenho(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Saldo a liquidar (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={saldoLiquidar}
                    onChange={(e) => setSaldoLiquidar(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Empenhado 1º grau (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={empenho1Grau}
                    onChange={(e) => setEmpenho1Grau(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Empenhado 2º grau (R$)</label>
                  <input
                    type="number"
                    className={CLASSE_DE_CAMPO}
                    placeholder="0,00"
                    min="0"
                    step="0.01"
                    value={empenho2Grau}
                    onChange={(e) => setEmpenho2Grau(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Franquia</label>
                  <select
                    className={CLASSE_DE_CAMPO}
                    value={franquiaTipo}
                    onChange={(e) => setFranquiaTipo(e.target.value as EntitlementKind | '')}
                  >
                    <option value="">Sem franquia</option>
                    <option value="HOURS">Horas</option>
                    <option value="TICKETS">Chamados</option>
                  </select>
                </div>
                {franquiaTipo && (
                  <div>
                    <label className="mb-1 block text-sm font-medium">
                      Quantidade ({franquiaTipo === 'HOURS' ? 'h/mês' : 'cham./mês'})
                    </label>
                    <input
                      type="number"
                      className={CLASSE_DE_CAMPO}
                      min="1"
                      value={franquiaQtd}
                      onChange={(e) => setFranquiaQtd(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="checkbox"
                  id="wiz-auto-renov"
                  checked={autoRenovacao}
                  onChange={(e) => setAutoRenovacao(e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                />
                <label htmlFor="wiz-auto-renov" className="cursor-pointer text-sm font-medium">
                  Auto-renovação
                </label>
                {autoRenovacao && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      className={cn(CLASSE_DE_CAMPO, 'w-20')}
                      min="1"
                      max="120"
                      value={mesesRenovacao}
                      onChange={(e) => setMesesRenovacao(e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground">meses</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── 3: Serviços ───────────────────────────────────────────────────── */}
          {etapa === 3 && (
            <>
              <p className="text-sm text-muted-foreground">
                Selecione os serviços cobertos e, opcionalmente, a política de ANS de cada um.
                Esta etapa pode ser preenchida depois na ficha do contrato.
              </p>
              <div className="space-y-1">
                {!catalogoServicos && (
                  <p className="py-4 text-center text-sm text-muted-foreground">Carregando catálogo…</p>
                )}
                {catalogoServicos?.filter((s) => s.active).map((servico) => {
                  const sel = servicosContrato.find((s) => s.serviceId === servico.id);
                  return (
                    <div
                      key={servico.id}
                      className={cn(
                        'flex flex-wrap items-center gap-3 rounded-md px-3 py-2 transition-colors',
                        sel ? 'bg-primary/5 ring-1 ring-inset ring-primary/20' : 'hover:bg-muted',
                      )}
                    >
                      <input
                        type="checkbox"
                        id={`wiz-svc-${servico.id}`}
                        checked={!!sel}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setServicosContrato((prev) => [...prev, { serviceId: servico.id, slaPolicyId: '' }]);
                          } else {
                            setServicosContrato((prev) => prev.filter((s) => s.serviceId !== servico.id));
                          }
                        }}
                        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                      />
                      <label
                        htmlFor={`wiz-svc-${servico.id}`}
                        className="flex-1 cursor-pointer text-sm font-medium"
                      >
                        {servico.name}
                        {servico.serviceType === 'TECHNICAL' && (
                          <span className="ml-2 text-[11px] text-muted-foreground">Interno</span>
                        )}
                      </label>
                      {sel && (
                        <select
                          value={sel.slaPolicyId}
                          onChange={(e) =>
                            setServicosContrato((prev) =>
                              prev.map((s) =>
                                s.serviceId === servico.id ? { ...s, slaPolicyId: e.target.value } : s,
                              ),
                            )
                          }
                          className={cn(CLASSE_DE_CAMPO, 'w-56 text-sm')}
                          aria-label={`ANS para ${servico.name}`}
                        >
                          <option value="">Sem ANS</option>
                          {politicasSla?.items
                            .filter((p) => p.contractual && p.active)
                            .map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                      )}
                    </div>
                  );
                })}
                {catalogoServicos?.filter((s) => s.active).length === 0 && (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    Nenhum serviço ativo no catálogo.
                  </p>
                )}
              </div>

              {/* Mini-form: novo serviço */}
              {novoServico ? (
                <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2">
                  <p className="text-xs font-semibold text-primary">Novo serviço</p>
                  <input
                    type="text"
                    placeholder="Nome do serviço"
                    value={nomeServico}
                    onChange={(e) => setNomeServico(e.target.value)}
                    className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                    autoFocus
                  />
                  <select
                    value={tipoServico}
                    onChange={(e) => setTipoServico(e.target.value as 'BUSINESS' | 'TECHNICAL')}
                    className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                  >
                    <option value="BUSINESS">Ao cliente</option>
                    <option value="TECHNICAL">Interno</option>
                  </select>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!nomeServico.trim() || mutacaoServico.isPending}
                      onClick={() => mutacaoServico.mutate()}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    >
                      {mutacaoServico.isPending ? 'Salvando…' : 'Criar e selecionar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNovoServico(false); setNomeServico(''); }}
                      className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                    >
                      Cancelar
                    </button>
                    {mutacaoServico.isError && (
                      <p className="text-xs text-destructive">{mensagemDeErro(mutacaoServico.error)}</p>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setNovoServico(true)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <Plus className="h-3 w-3" /> Cadastrar novo serviço
                </button>
              )}

              {/* Mini-form: nova política de ANS */}
              {novaSla ? (
                <div className="rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2">
                  <p className="text-xs font-semibold text-primary">Nova política de ANS</p>
                  <input
                    type="text"
                    placeholder="Nome da política"
                    value={nomeSla}
                    onChange={(e) => setNomeSla(e.target.value)}
                    className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                    autoFocus
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={kindSla}
                      onChange={(e) => setKindSla(e.target.value as 'SLA' | 'OLA' | 'UC')}
                      className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                    >
                      <option value="SLA">SLA (cliente)</option>
                      <option value="OLA">OLA (interno)</option>
                      <option value="UC">UC (fornecedor)</option>
                    </select>
                    <select
                      value={metricaSla}
                      onChange={(e) => setMetricaSla(e.target.value as 'FIRST_RESPONSE' | 'RESOLUTION' | 'UPDATE_INTERVAL')}
                      className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                    >
                      <option value="FIRST_RESPONSE">Primeira resposta</option>
                      <option value="RESOLUTION">Resolução</option>
                      <option value="UPDATE_INTERVAL">Atualização</option>
                    </select>
                  </div>
                  <input
                    type="number"
                    min="1"
                    placeholder="Alvo em minutos (ex: 480)"
                    value={alvoSla}
                    onChange={(e) => setAlvoSla(e.target.value)}
                    className={cn(CLASSE_DE_CAMPO, 'text-sm')}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!nomeSla.trim() || !alvoSla || mutacaoSla.isPending}
                      onClick={() => mutacaoSla.mutate()}
                      className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    >
                      {mutacaoSla.isPending ? 'Salvando…' : 'Criar ANS'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNovaSla(false); setNomeSla(''); setAlvoSla(''); }}
                      className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                    >
                      Cancelar
                    </button>
                    {mutacaoSla.isError && (
                      <p className="text-xs text-destructive">{mensagemDeErro(mutacaoSla.error)}</p>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setNovaSla(true)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <Plus className="h-3 w-3" /> Cadastrar nova política de ANS
                </button>
              )}

              {servicosContrato.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {servicosContrato.length} serviço(s) selecionado(s).
                </p>
              )}
            </>
          )}

          {/* ── 4: Partes e Documentos ────────────────────────────────────────── */}
          {etapa === 4 && (
            <div className="space-y-6">

              {/* Prepostos Aptum */}
              <div>
                <h3 className="mb-2 text-sm font-semibold">Prepostos Aptum</h3>
                {prepostos.length > 0 && (
                  <ul className="mb-3 space-y-1">
                    {prepostos.map((p) => (
                      <li
                        key={p._lid}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 truncate">
                          <span className="font-medium">{p.name}</span>
                          {p.email && <span className="ml-2 text-muted-foreground">{p.email}</span>}
                          {p.phone && <span className="ml-2 text-muted-foreground">{p.phone}</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setPartes((prev) => prev.filter((q) => q._lid !== p._lid))}
                          className="flex-none text-muted-foreground transition-colors hover:text-destructive"
                          aria-label="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2">
                  <select
                    className={CLASSE_DE_CAMPO}
                    value={prepOpId}
                    onChange={(e) => setPrepOpId(e.target.value)}
                  >
                    <option value="">Selecionar operador (opcional)…</option>
                    {operadores?.items.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Nome *"
                      value={prepNome}
                      onChange={(e) => setPrepNome(e.target.value)}
                    />
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="E-mail"
                      value={prepEmail}
                      onChange={(e) => setPrepEmail(e.target.value)}
                    />
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Telefone"
                      value={prepTel}
                      onChange={(e) => setPrepTel(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={adicionarPreposto}
                    disabled={!prepNome.trim()}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                    Adicionar preposto
                  </Button>
                </div>
              </div>

              {/* Fiscais do Cliente */}
              <div>
                <h3 className="mb-2 text-sm font-semibold">Fiscais do Cliente</h3>
                {fiscais.length > 0 && (
                  <ul className="mb-3 space-y-1">
                    {fiscais.map((p) => (
                      <li
                        key={p._lid}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 truncate">
                          <span className="font-medium">{p.name}</span>
                          {p.isPrimary && (
                            <span className="ml-2 rounded-full bg-success/15 px-1.5 py-0.5 text-[11px] font-semibold text-success">
                              Titular
                            </span>
                          )}
                          {p.email && <span className="ml-2 text-muted-foreground">{p.email}</span>}
                          {p.phone && <span className="ml-2 text-muted-foreground">{p.phone}</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setPartes((prev) => prev.filter((q) => q._lid !== p._lid))}
                          className="flex-none text-muted-foreground transition-colors hover:text-destructive"
                          aria-label="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Nome *"
                      value={fiscNome}
                      onChange={(e) => setFiscNome(e.target.value)}
                    />
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="E-mail"
                      value={fiscEmail}
                      onChange={(e) => setFiscEmail(e.target.value)}
                    />
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Telefone"
                      value={fiscTel}
                      onChange={(e) => setFiscTel(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="wiz-fisc-titular"
                        checked={fiscTitular}
                        onChange={(e) => setFiscTitular(e.target.checked)}
                        className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                        disabled={fiscais.some((p) => p.isPrimary)}
                      />
                      <label htmlFor="wiz-fisc-titular" className="cursor-pointer text-sm">Titular</label>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={adicionarFiscal}
                      disabled={!fiscNome.trim()}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                      Adicionar fiscal
                    </Button>
                  </div>
                </div>
              </div>

              {/* Demandantes */}
              <div>
                <h3 className="mb-2 text-sm font-semibold">Demandantes</h3>
                {partes.filter((p) => p.role === 'DEMANDANTE').length > 0 && (
                  <ul className="mb-3 space-y-1">
                    {partes.filter((p) => p.role === 'DEMANDANTE').map((p) => (
                      <li
                        key={p._lid}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 truncate">
                          <span className="font-medium">{p.name}</span>
                          {p.isPrimary && (
                            <span className="ml-2 rounded-full bg-success/15 px-1.5 py-0.5 text-[11px] font-semibold text-success">
                              Titular
                            </span>
                          )}
                          {p.email && <span className="ml-2 text-muted-foreground">{p.email}</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setPartes((prev) => prev.filter((q) => q._lid !== p._lid))}
                          className="flex-none text-muted-foreground transition-colors hover:text-destructive"
                          aria-label="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <DemandanteForm
                  jaTemTitular={partes.some((p) => p.role === 'DEMANDANTE' && p.isPrimary)}
                  aoAdicionar={(d) => setPartes((prev) => [...prev, { ...d, _lid: nextLid() }])}
                />
              </div>

              {/* Documentos */}
              <div>
                <h3 className="mb-2 text-sm font-semibold">Documentos</h3>
                {anexos.length > 0 && (
                  <ul className="mb-3 space-y-1">
                    {anexos.map((a) => (
                      <li
                        key={a._lid}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 truncate">
                          <span className="font-medium">{a.label}</span>
                          <span className="ml-2 truncate text-muted-foreground">{a.url}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAnexos((prev) => prev.filter((q) => q._lid !== a._lid))}
                          className="flex-none text-muted-foreground transition-colors hover:text-destructive"
                          aria-label="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2">
                    <select
                      className={CLASSE_DE_CAMPO}
                      value={anexTipo}
                      onChange={(e) => setAnexTipo(e.target.value as ContractAttachmentKind)}
                    >
                      {WIZARD_ATTACHMENT_KINDS.map((k) => (
                        <option key={k.valor} value={k.valor}>{k.rotulo}</option>
                      ))}
                    </select>
                    <input
                      className={CLASSE_DE_CAMPO}
                      placeholder="Rótulo"
                      value={anexRotulo}
                      onChange={(e) => setAnexRotulo(e.target.value)}
                    />
                    <input
                      className={cn(CLASSE_DE_CAMPO, 'col-span-2')}
                      placeholder="URL do documento *"
                      value={anexUrl}
                      onChange={(e) => setAnexUrl(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={adicionarAnexoLocal}
                    disabled={!anexUrl.trim()}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
                    Adicionar documento
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Erro */}
          {erro && (
            <p className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {erro}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-5 py-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => (etapa > 1 ? setEtapa((e) => e - 1) : aoFechar())}
            disabled={enviando}
          >
            {etapa > 1 && <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />}
            {etapa > 1 ? 'Voltar' : 'Cancelar'}
          </Button>
          <div className="flex items-center gap-2">
            {etapa < 4 ? (
              <Button
                type="button"
                onClick={() => setEtapa((e) => e + 1)}
                disabled={(etapa === 1 && !etapa1Valida) || (etapa === 2 && !etapa2Valida)}
              >
                Próximo
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
              </Button>
            ) : (
              <Button
                type="button"
                loading={enviando}
                disabled={!etapa1Valida || !etapa2Valida}
                onClick={submeter}
              >
                Criar contrato
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
